import { CanceledError } from 'axios';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { clearAuthTokens, handleAuthError } from '../utils/authErrorHandler';
import {
  getViewAsToken,
  isViewAs,
  markViewAsEnded,
  VIEW_AS_READ_ONLY_CODE,
  VIEW_AS_READ_ONLY_EVENT,
} from '../viewAs/viewAsSession';

import { getActivePracticeScope } from './practiceScope';
import { HEALIO_REQUEST_HEADER, HEALIO_VIEW_AS_HEADER, loadServerSession } from './sessionAPI';

interface RetryableConfig extends InternalAxiosRequestConfig {
  _sessionRetry?: boolean;
}

// Single shared session check across the entire app — if five different
// axios instances get 401 simultaneously we still only ask the API once.
let sharedCheckPromise: Promise<boolean> | null = null;

// Circuit breaker: if the check has failed recently, don't hammer the API.
// A belt-and-suspenders guard against cascading failures (downstream 401s
// on a live session, etc.) that could otherwise spam checks while the
// browser is navigating to /login.
const CHECK_FAILURE_WINDOW_MS = 30_000;
const CHECK_FAILURE_MAX = 3;
const checkFailureTimestamps: number[] = [];

const recordCheckFailure = () => {
  const now = Date.now();

  checkFailureTimestamps.push(now);
  // Drop anything outside the window.
  while (
    checkFailureTimestamps.length > 0 &&
    now - checkFailureTimestamps[0] > CHECK_FAILURE_WINDOW_MS
  ) {
    checkFailureTimestamps.shift();
  }
};

const isCircuitBreakerTripped = (): boolean => {
  const now = Date.now();

  while (
    checkFailureTimestamps.length > 0 &&
    now - checkFailureTimestamps[0] > CHECK_FAILURE_WINDOW_MS
  ) {
    checkFailureTimestamps.shift();
  }

  return checkFailureTimestamps.length >= CHECK_FAILURE_MAX;
};

/**
 * HA-445: whether the session is still live (GET /auth/session). The API refreshes the session's
 * tokens itself, so a 401 on a live session is rare; one that has ended can't be revived here.
 */
const performSharedSessionCheck = (): Promise<boolean> => {
  if (isCircuitBreakerTripped()) {
    console.warn(
      '[session] Session check circuit breaker tripped — too many failures in the last 30s. Failing fast.'
    );

    return Promise.resolve(false);
  }
  if (!sharedCheckPromise) {
    sharedCheckPromise = loadServerSession()
      .then((session) => {
        if (!session) recordCheckFailure();

        return session !== null;
      })
      .finally(() => {
        sharedCheckPromise = null;
      });
  }

  return sharedCheckPromise;
};

/** Reset the circuit breaker after a successful login. */
export const resetSessionInterceptorState = () => {
  checkFailureTimestamps.length = 0;
  sharedCheckPromise = null;
};

/** Return true if the app is currently on the login page. */
export const MFA_SETUP_PATH = '/mfa/setup';

export const isMfaRequired = (error: AxiosError): boolean =>
  error.response?.status === 403 &&
  (error.response.data as { code?: unknown } | undefined)?.code === 'MFA_REQUIRED';

/** HA-447: the API refused a write in a support view-as session. */
export const isViewAsReadOnly = (error: AxiosError): boolean =>
  error.response?.status === 403 &&
  (error.response.data as { code?: unknown } | undefined)?.code === VIEW_AS_READ_ONLY_CODE;

const isOnLoginPage = (): boolean => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.toLowerCase();

  return path === '/login' || path === '/logout';
};

/**
 * Attaches the session-aware request + response interceptors to an axios
 * instance. Replaces the scattered per-file boilerplate across src/api/*.
 *
 * Request interceptor (HA-445): sends the session cookie and X-Healio-Request,
 * which the API requires on every cookie-authenticated write (CSRF). No token:
 * the API holds it.
 * Response interceptor: on 401, checks once whether the session is still live
 * and replays the original request if so; otherwise it goes to
 * `/login?reason=session_expired`.
 */
export const installSessionInterceptors = (api: AxiosInstance): void => {
  api.interceptors.request.use(
    async (config) => {
      // HA-447: a support view-as tab sends its session token, never the admin's cookie, and
      // has nothing to refresh. Once the session has ended nothing is sent at all (a promise
      // chain can outlive the app's unmount). The API signs in from the bearer only (HA-454), so
      // a request without one gets 401; this keeps it from leaving the tab in the first place.
      if (isViewAs()) {
        const viewAsToken = getViewAsToken();

        if (!viewAsToken) {
          throw new CanceledError('The support session has ended');
        }
        config.headers.Authorization = `Bearer ${viewAsToken}`;
        // HA-445: the API never signs a marked request in from the session cookie, which here
        // is the admin's (same-origin it goes along whatever withCredentials says).
        config.headers[HEALIO_VIEW_AS_HEADER] = '1';
        // Cross-site, this keeps the admin's cookies off the request too.
        config.withCredentials = false;

        return config;
      }

      // HA-445: the HttpOnly session cookie authenticates the request (the API refreshes the
      // session's tokens itself). X-Healio-Request is what lets a cookie-authenticated write
      // through: a cross-site form or link can't set it (CSRF).
      config.withCredentials = true;
      config.headers[HEALIO_REQUEST_HEADER] = '1';

      // HA-320: scope GET reads to the multi-practice patient's selected
      // non-primary practice. GETs only — POST/PUT/DELETE bodies may carry
      // their own practice identity, and double-scoping would contradict
      // it. getActivePracticeScope() returns null for the primary practice
      // (backend falls back to the JWT primary) and for non-Client users.
      if (config.method?.toLowerCase() === 'get') {
        const practiceScope = getActivePracticeScope();

        if (practiceScope) {
          config.params = { ...(config.params ?? {}), practice_id: practiceScope };
        }
      }

      return config;
    },
    (error) => Promise.reject(error instanceof Error ? error : new Error(String(error)))
  );

  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // HA-447: in a view-as tab a 401 means the session is over (ended, expired or revoked).
      // It is never refreshed or replayed - a replay would carry the admin's cookie - and the
      // admin's cookies are never cleared from here.
      if (isViewAs()) {
        if (error.response?.status === 401) {
          markViewAsEnded();
        } else if (isViewAsReadOnly(error)) {
          window.dispatchEvent(new Event(VIEW_AS_READ_ONLY_EVENT));
        }

        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }

      const originalConfig = error.config as RetryableConfig | undefined;
      const is401 = error.response?.status === 401;

      // HA-440: the API gates SystemAdmin endpoints on MFA and answers 403
      // { code: 'MFA_REQUIRED' } until it's set up - take the user to setup.
      if (isMfaRequired(error) && window.location.pathname !== MFA_SETUP_PATH) {
        window.location.assign(MFA_SETUP_PATH);
      }

      // Short-circuit on non-401s and on replays that already came back 401.
      if (!is401 || !originalConfig || originalConfig._sessionRetry) {
        if (is401) {
          clearAuthTokens();
          handleAuthError('session_expired');
        }

        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }

      // Already on /login — don't check, just fail. Prevents a loop where the
      // login page itself is making authenticated requests that 401 and
      // re-trigger checks.
      if (isOnLoginPage()) {
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }

      originalConfig._sessionRetry = true;

      const live = await performSharedSessionCheck();

      if (!live) {
        clearAuthTokens();
        handleAuthError('session_expired');

        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }

      return api(originalConfig);
    }
  );
};
